import { useTransition, useMemo, useState, useCallback, useOptimistic } from 'react';
import {
  Icon,
  Badge,
  Flex,
  Box,
  Heading,
  Text,
  HStack,
  Stack,
  AccordionItemBody,
} from '@chakra-ui/react';
import {
  AccordionRoot,
  AccordionItem,
  AccordionItemContent,
  AccordionItemTrigger,
} from '@/components/ui/accordion';
import { ClockIcon, DocumentTextIcon } from '@heroicons/react/20/solid';
import { toaster } from '@/components/ui/toaster';
import { Prose } from '@/components/ui/prose';
import ContentDrawer from '@/pages/contents/content-drawer';
import useModalToggle from '@/lib/hooks/use-modal-toggle';
import { Content, GroupForm } from '@/lib/types';
import { baseApi } from '@/utils/api';

interface ContentItem {
  id: string;
  name: string;
  content_type_name: string;
  thumbnail?: string;
}

interface ContentMap {
  [referenceId: string]: ContentItem;
}

interface Props {
  block: {
    type: number;
    label: string;
  };
  content: any;
  subContents?: ContentMap;
  isPublic?: boolean;
}

export default function SectionBlock({ block, content, subContents, isPublic = false }: Props) {
  const contentDrawer = useModalToggle();

  const [selectedContent, setSelectedContent] = useState<Content | null>(null);
  const [isPending, startTransition] = useTransition();
  const [viewingState, setViewingContent] = useOptimistic(
    { isViewing: false, referenceId: null as string | null },
    (state, action: { viewing: boolean; id: string | null }) => ({
      isViewing: action.viewing,
      referenceId: action.id,
    }),
  );

  const formatDuration = useCallback((minutes: number): string => {
    const mins = Math.floor(minutes / 60);
    const secs = minutes % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }, []);

  const hasContent = useMemo(() => Array.isArray(content) && content.length > 0, [content]);

  const sections = useMemo(() => {
    if (!hasContent) return [];

    return content.map((item: any, index: number) => ({
      ...item,
      formattedDuration: formatDuration(item.duration || 0),
      id: `section-${index}`,
      hasGroups: Array.isArray(item.groups) && item.groups.length > 0,
    }));
  }, [content, hasContent, formatDuration]);

  const handleView = useCallback(
    async (referenceId: string) => {
      if (isPending || (viewingState.isViewing && viewingState.referenceId === referenceId)) return;

      startTransition(() => {
        setViewingContent({ viewing: true, id: referenceId });
      });

      try {
        const response = await baseApi.get(`public/content/${referenceId}`);
        const content = response.data.data;

        if (!content) {
          toaster.create({
            type: 'error',
            title: 'Content not found',
            duration: 3000,
          });
          return;
        }

        setSelectedContent(content);
        contentDrawer.open();
      } catch (err: any) {
        toaster.create({
          type: 'error',
          title: err.response?.data?.error || 'Failed to fetch content',
        });
      } finally {
        startTransition(() => {
          setViewingContent({ viewing: false, id: null });
        });
      }
    },
    [contentDrawer, setViewingContent, viewingState, isPending, startTransition],
  );

  if (!hasContent) {
    return (
      <Box>
        <Heading as='h3' fontSize='md' mb={4}>
          {block.label}
        </Heading>
        <Box
          p={4}
          borderWidth='1px'
          borderStyle='dashed'
          borderRadius='md'
          textAlign='center'
          color='gray.500'
        >
          <Icon as={DocumentTextIcon} w={6} h={6} mb={2} />
          <Text>No content available</Text>
        </Box>
      </Box>
    );
  }

  const defaultValues = isPublic ? sections.map((_: unknown, index: number) => index + '') : ['0'];

  return (
    <Box>
      <Heading as={'h3'} fontSize={'xl'} mb={4}>
        {block.label}
      </Heading>

      <AccordionRoot defaultValue={defaultValues} multiple>
        {sections.map((item: any, index: number) => (
          <AccordionItem key={index} value={index + ''}>
            <AccordionItemTrigger>
              <Flex align='center' width='full' justify='space-between'>
                <HStack gap={2}>
                  <Badge size='sm' colorScheme='blue' borderRadius='full'>
                    <Icon as={ClockIcon} w={3} h={3} mr={1} />
                    <Text fontSize='xs'>{item.formattedDuration}</Text>
                  </Badge>
                  <Text fontSize={'md'} fontWeight={'semibold'}>
                    {item.name}
                  </Text>
                </HStack>
              </Flex>
            </AccordionItemTrigger>

            <AccordionItemContent>
              <AccordionItemBody px={4} pb={4}>
                {item.notes && (
                  <Prose size='md'>
                    <div dangerouslySetInnerHTML={{ __html: item.notes }} />
                  </Prose>
                )}

                {item.hasGroups && (
                  <Stack mt={3}>
                    {item.groups.map((group: GroupForm, groupIndex: number) => (
                      <Box key={`group-${groupIndex}`} py={1} px={4}>
                        <Flex align='center' justify='space-between'>
                          <Text fontSize={'sm'} fontWeight={'medium'}>
                            {group?.name}
                          </Text>
                        </Flex>

                        {group.contents && group.contents.length > 0 && (
                          <Stack mt={3}>
                            {group.contents.map((contentRef: string) => {
                              const content = subContents && subContents[contentRef];

                              if (!content || !content?.name) return null;

                              return (
                                <Box
                                  key={contentRef}
                                  py={1}
                                  px={4}
                                  bg={'gray.100'}
                                  borderRadius={'md'}
                                  transition='all 0.2s'
                                  _hover={{
                                    cursor: 'pointer',
                                    bg: 'gray.200',
                                    transform: 'translateY(-1px)',
                                  }}
                                  onClick={() => handleView(contentRef)}
                                >
                                  <Flex align='center' justify='space-between'>
                                    <Box>
                                      <Text fontSize={'sm'} fontWeight={'medium'}>
                                        {content.name}
                                      </Text>
                                      <Text fontSize={'xs'} fontStyle={'italic'}>
                                        {content.content_type_name}
                                      </Text>
                                    </Box>
                                  </Flex>
                                </Box>
                              );
                            })}
                          </Stack>
                        )}
                      </Box>
                    ))}
                  </Stack>
                )}
              </AccordionItemBody>
            </AccordionItemContent>
          </AccordionItem>
        ))}
      </AccordionRoot>

      {selectedContent && (
        <ContentDrawer
          {...contentDrawer.props}
          content={selectedContent}
          drawerId={'section-drawer'}
        />
      )}
    </Box>
  );
}
